import type { Department } from '@clockwise/clients/departments';
import { CheckRow, Combobox } from '@/components/custom/combobox';
import { departmentPickerConfig } from '@/components/custom/entity-pickers';
import { PickerTruncationNotice } from '@/components/custom/picker-truncation-notice';
import {
  type LoadStrategy,
  useEntityAutocomplete,
} from '@/components/custom/use-entity-autocomplete';

// Picker for a department. Defaults to 'lazy' (server-side `q` search) so a
// tenant with more departments than the preload page cap (200) can still find
// and select every one — 'preload' fetched a single capped page and filtered
// in memory, silently hiding any past the cap. Backend `q` ORs ILIKE across
// name and code. Pass `loadStrategy="preload"` for a known-small catalog.
interface Props {
  value: string | null;
  onChange: (department: Department | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  loadStrategy?: LoadStrategy;
}

// List/detail fetch + key contract is shared with EntityMultiSelect via
// `entity-pickers` so the two never compute divergent query keys.
const {
  getId: getDeptId,
  getLabel: labelOf,
  fetchList: fetchDeptList,
  fetchOne: fetchDeptOne,
  listKey: deptListKey,
  detailKey: deptDetailKey,
  lazyLimit: deptLazyLimit,
  maxLimit: deptMaxLimit,
} = departmentPickerConfig;
const deptFilter = (d: Department, q: string) => {
  const hay = `${d.name} ${d.code}`.toLowerCase();
  return hay.includes(q.toLowerCase());
};

export function DepartmentAutocomplete({
  value,
  onChange,
  disabled,
  placeholder = 'Select department…',
  className,
  loadStrategy = 'lazy',
}: Props) {
  const auto = useEntityAutocomplete<Department>({
    value,
    strategy: loadStrategy,
    fetchList: fetchDeptList,
    fetchOne: fetchDeptOne,
    listKey: deptListKey,
    detailKey: deptDetailKey,
    lazyLimit: deptLazyLimit,
    maxLimit: deptMaxLimit,
  });

  return (
    <Combobox<Department>
      items={auto.items}
      selectedItem={auto.selectedItem}
      value={value}
      onChange={onChange}
      getId={getDeptId}
      getLabel={labelOf}
      filterFn={auto.isClient ? deptFilter : undefined}
      onQueryChange={auto.isClient ? undefined : auto.setQuery}
      onOpenChange={auto.setOpen}
      loading={auto.loading}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      renderFooter={
        auto.truncated
          ? () => (
              <PickerTruncationNotice
                truncated={auto.truncated}
                noun="departments"
                clientFiltered={auto.isClient}
              />
            )
          : undefined
      }
      renderItem={(d, { isSelected }) => (
        <CheckRow isSelected={isSelected} label={d.name} secondary={d.code} />
      )}
    />
  );
}
